import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import FinalCTA from "@/components/FinalCTA";
import SectionHeading from "@/components/SectionHeading";
import { Briefcase, MapPin, Clock, ArrowRight, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Careers() {
  const [openings, setOpenings] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", cover_letter: "", portfolio_url: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/careers/openings`).then((res) => setOpenings(res.data.openings || [])).catch(() => setOpenings([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedRole) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/careers/apply`, { ...form, role: selectedRole.title });
      toast.success("Application submitted! Our team will be in touch.");
      setForm({ full_name: "", email: "", phone: "", cover_letter: "", portfolio_url: "" });
      setSelectedRole(null);
    } catch (err) {
      toast.error("Could not submit application. Please email secure@defence.cngtpl.com.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="careers-page">
      <PageHero
        eyebrow="Careers"
        title="Build the Future of Cybersecurity With Us"
        description="Join a team that protects 500+ organizations and millions of citizens across India. We're hiring across SOC, engineering, awareness and internships."
      />

      <section className="py-20 bg-white">
        <div className="container-cd">
          <SectionHeading eyebrow="Open Roles" title="We're hiring" />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {openings.map((o, i) => (
              <div key={o.id} className="card-cd flex flex-col" data-testid={`career-opening-${i}`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display text-xl font-bold text-[#0A1B5E]">{o.title}</h3>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500 font-mono-tech tracking-wide">
                      <span className="inline-flex items-center gap-1"><MapPin size={14} /> {o.location}</span>
                      <span className="inline-flex items-center gap-1"><Clock size={14} /> {o.type}</span>
                    </div>
                  </div>
                  <Briefcase size={22} className="text-[#3B82F6] shrink-0" />
                </div>
                <p className="mt-4 text-slate-600 text-sm leading-relaxed">{o.description}</p>
                <button
                  onClick={() => setSelectedRole(o)}
                  data-testid={`career-apply-btn-${i}`}
                  className="btn-accent mt-6 self-start text-sm"
                >
                  Apply Now <ArrowRight size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {selectedRole && (
        <div className="fixed inset-0 z-[80] grid place-items-center bg-[#0A1B5E]/40 backdrop-blur-sm p-4" data-testid="career-apply-modal">
          <div className="bg-white rounded-2xl max-w-xl w-full p-8 shadow-2xl relative">
            <button
              onClick={() => setSelectedRole(null)}
              className="absolute top-4 right-4 h-9 w-9 grid place-items-center rounded-full hover:bg-slate-100"
              data-testid="career-modal-close"
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <span className="badge-cd">Apply for</span>
            <h3 className="mt-3 font-display text-2xl font-bold text-[#0A1B5E]">{selectedRole.title}</h3>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <input data-testid="career-input-name" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Full name" className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent outline-none" />
              <input data-testid="career-input-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent outline-none" />
              <input data-testid="career-input-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent outline-none" />
              <input data-testid="career-input-portfolio" value={form.portfolio_url} onChange={(e) => setForm({ ...form, portfolio_url: e.target.value })} placeholder="LinkedIn / Portfolio URL" className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent outline-none" />
              <textarea data-testid="career-input-cover" value={form.cover_letter} onChange={(e) => setForm({ ...form, cover_letter: e.target.value })} placeholder="Tell us why you're a great fit" rows={4} className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent outline-none resize-none" />
              <button type="submit" disabled={submitting} data-testid="career-submit-btn" className="btn-accent w-full justify-center">
                {submitting ? "Submitting..." : "Submit Application"} <ArrowRight size={16} />
              </button>
            </form>
          </div>
        </div>
      )}

      <FinalCTA />
    </div>
  );
}
